<template>
  <v-card class="notification-dropdown" elevation="8" rounded="lg">
    <!-- Header -->
    <v-card-title class="dropdown-header">
      <div class="header-content">
        <span class="header-title">Notifications</span>
        <v-chip
          v-if="unreadCount > 0"
          color="primary"
          size="small"
          class="unread-chip"
        >
          {{ unreadCount }}
        </v-chip>
      </div>
      <v-btn
        v-if="unreadCount > 0"
        variant="text"
        size="small"
        @click="markAllAsRead"
        :loading="markingAllRead"
      >
        Mark all as read
      </v-btn>
    </v-card-title>

    <v-divider />

    <!-- Notifications List -->
    <v-card-text class="dropdown-content">
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
        class="mb-2"
      />

      <div v-if="notifications.length > 0" class="notifications-list">
        <NotificationItem
          v-for="notification in notifications"
          :key="notification.id"
          :notification="notification"
          @markAsRead="handleMarkAsRead"
          @delete="handleDelete"
        />
      </div>

      <div v-else class="empty-state">
        <v-icon icon="mdi-bell-outline" size="48" color="grey-lighten-1" />
        <p class="empty-text">No notifications yet</p>
      </div>
    </v-card-text>

    <v-divider />

    <!-- Footer -->
    <v-card-actions class="dropdown-footer">
      <v-btn
        block
        variant="text"
        color="primary"
        @click="viewAll"
      >
        View All Notifications
        <v-icon icon="mdi-arrow-right" end />
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationStore } from '@/store/notifications'
import NotificationItem from './NotificationItem.vue'

const router = useRouter()
const notificationStore = useNotificationStore()

const markingAllRead = ref(false)

// Computed properties
const notifications = computed(() => notificationStore.recentNotifications)
const unreadCount = computed(() => notificationStore.unreadCount)
const loading = computed(() => notificationStore.loading)

// Methods
const handleMarkAsRead = async (notificationId: string) => {
  try {
    await notificationStore.markAsRead(notificationId)
  } catch (error) {
    console.error('Failed to mark notification as read:', error)
  }
}

const handleDelete = async (notificationId: string) => {
  try {
    await notificationStore.deleteNotification(notificationId)
  } catch (error) {
    console.error('Failed to delete notification:', error)
  }
}

const markAllAsRead = async () => {
  markingAllRead.value = true
  try {
    await notificationStore.markAllAsRead()
  } catch (error) {
    console.error('Failed to mark all as read:', error)
  } finally {
    markingAllRead.value = false
  }
}

const viewAll = () => {
  router.push('/notifications')
}
</script>

<style scoped lang="scss">
.notification-dropdown {
  width: 420px;
  max-width: 90vw;
  max-height: 600px;
  display: flex;
  flex-direction: column;
}

.dropdown-header {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  .header-content {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .header-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #1a1a1a;
  }

  .unread-chip {
    font-weight: 600;
  }
}

.dropdown-content {
  padding: 0;
  overflow-y: auto;
  max-height: 480px;
  flex: 1;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
      background: #a8a8a8;
    }
  }
}

.notifications-list {
  display: flex;
  flex-direction: column;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;

  .empty-text {
    margin-top: 16px;
    font-size: 0.95rem;
    color: #666;
  }
}

.dropdown-footer {
  padding: 12px;
}

@media (max-width: 600px) {
  .notification-dropdown {
    width: 100vw;
    max-width: 100vw;
    border-radius: 0;
  }

  .dropdown-header {
    padding: 12px 16px;

    .header-title {
      font-size: 1rem;
    }
  }

  .dropdown-content {
    max-height: 400px;
  }
}
</style>
